import React, { useCallback, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import jsPDF from "jspdf";
import { safeFetchAll, MONTH_NAMES } from "./constants";
import { premisesApiResponse } from "../../types/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Range = "1M" | "3M" | "6M" | "12M";

interface MonthPoint {
  label: string;
  extACatNcPct: number | null; intACatNcPct: number | null;
  avgPct: number | null; nearMissPct: number | null; nearMissActual: number | null;
  majorPct: number | null; majorActual: number | null;
  extA: number | null; extB: number | null; extC: number | null;
  intA: number | null; intB: number | null; intC: number | null;
  extAvgPct: number | null; intAvgPct: number | null;
  elecAvgPct: number | null;
}

const ml = (m: number, y: number) => MONTH_NAMES[m - 1].slice(0, 3) + " " + String(y).slice(2);

const getMonths = (range: Range): { month: number; year: number }[] => {
  const count = range === "1M" ? 1 : range === "3M" ? 3 : range === "6M" ? 6 : 12;
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1);
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  });
};

const getMonthYear = (s: string): { month: number; year: number } | null => {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

const avg = (vals: (number | null | undefined)[]): number | null => {
  const v = vals.filter((x): x is number => x != null);
  return v.length ? v.reduce((s, n) => s + n, 0) / v.length : null;
};

// ── Premise-record matching ──────────────────────────────────────────────────
// Some scorecard endpoints don't actually filter server-side by
// premisesId/premise query params — when that happens every premise gets
// back the exact same full dataset, which is why identical numbers were
// showing up for every premise in the PDF. This checks the record itself
// for a premise reference and keeps only the ones that match the premise
// we're currently building a page for. NOTE: adjust the candidate field
// names below if your scorecard schema uses a different key.
function belongsToPremise(record: any, premiseId: string): boolean {
  const candidates = [
    record.premisesId, record.premise, record.premiseId,
    record.premisesId?._id, record.premise?._id, record.premiseId?._id,
  ]
    .filter((v) => v != null)
    .map(String);
  // If the record carries no recognizable premise reference at all, we
  // can't safely filter it — leave it in rather than silently dropping
  // everything (but this is the case to check first if numbers still look
  // wrong: it means the field name here doesn't match your API's schema).
  if (candidates.length === 0) return true;
  return candidates.includes(String(premiseId));
}

// ── Data builders ──────────────────────────────────────────────────────────

function buildFirePoints(fsRecords: any[], months: { month: number; year: number }[]): MonthPoint[] {
  return months.map(({ month, year }) => {
    const recs = fsRecords.filter(r => { const my = getMonthYear(r.date || r.createdAt || ""); return my && my.month === month && my.year === year; });
    const scored = recs.filter(r => r.fireSafetyParameters?.length);
    const pcts = scored.map(r => { const p = r.fireSafetyParameters || []; const ts = p.reduce((s: number, x: any) => s + (x.score ?? 0), 0); const tm = p.reduce((s: number, x: any) => s + x.maxScore, 0); return tm > 0 ? (ts / tm) * 100 : null; });
    const catNcPct = (rs: any[], field: "externalAuditRecords" | "internalAuditRecords") => avg(rs.map(r => { const rec = (r[field] || []).find((a: any) => a.category === "A"); return rec?.completionPercent ?? null; }));
    const catScore = (rs: any[], field: "externalAuditRecords" | "internalAuditRecords", cat: string) => avg(rs.map(r => { const rec = (r[field] || []).find((a: any) => a.category === cat); return rec?.currentScore ?? null; }));
    const auditAvgPct = (rs: any[], field: "externalAuditRecords" | "internalAuditRecords") => avg(rs.map(r => { const cats = r[field] || []; const ts = cats.reduce((s: number, a: any) => s + (a.currentScore ?? 0), 0); const tm = cats.reduce((s: number, a: any) => s + (a.maxScore ?? 0), 0); return tm > 0 ? (ts / tm) * 100 : null; }));
    return {
      label: ml(month, year),
      extACatNcPct: catNcPct(recs, "externalAuditRecords"),
      intACatNcPct: catNcPct(recs, "internalAuditRecords"),
      avgPct: avg(pcts),
      nearMissPct: avg(scored.map(r => r.nearMissPct ?? null)),
      nearMissActual: scored.reduce((s, r) => s + (r.nearMissActual ?? 0), 0),
      majorPct: avg(scored.map(r => r.majorPct ?? null)),
      majorActual: scored.reduce((s, r) => s + (r.majorActual ?? 0), 0),
      extA: catScore(recs, "externalAuditRecords", "A"), extB: catScore(recs, "externalAuditRecords", "B"), extC: catScore(recs, "externalAuditRecords", "C"),
      intA: catScore(recs, "internalAuditRecords", "A"), intB: catScore(recs, "internalAuditRecords", "B"), intC: catScore(recs, "internalAuditRecords", "C"),
      extAvgPct: auditAvgPct(recs, "externalAuditRecords"),
      intAvgPct: auditAvgPct(recs, "internalAuditRecords"),
      elecAvgPct: null,
    };
  });
}

function mergeElecPoints(elecRecords: any[], months: { month: number; year: number }[], base: MonthPoint[]): MonthPoint[] {
  return months.map(({ month, year }, idx) => {
    const recs = elecRecords.filter(r => { const my = getMonthYear(r.date || r.createdAt || ""); return my && my.month === month && my.year === year; });
    const scored = recs.filter(r => r.sections?.length);
    const pcts = scored.map(r => { const s = r.sections || []; const ts = s.reduce((a: number, x: any) => a + x.totalScore, 0); const tm = s.reduce((a: number, x: any) => a + x.totalMaxScore, 0); return tm > 0 ? (ts / tm) * 100 : null; });
    return { ...base[idx], elecAvgPct: avg(pcts) };
  });
}

// ── Bar value label plugin — matches screen behaviour exactly ────────────────
// - Regular bars: show "62.5%" on top
// - Bars with _actuals: show "66.7% (2)" on top  (same as TrendsView)
// - Null values (no data): show "N/A" centred on the zero line
// - Audit score bars (_pct available): show "9.4\n(63%)" two-line label

const barLabelPlugin = {
  id: "barLabels",
  afterDatasetsDraw(chart: any) {
    try {
      const { ctx } = chart;
      chart.data.datasets.forEach((ds: any, di: number) => {
        const meta = chart.getDatasetMeta(di);
        if (!meta?.data?.length) return;
        meta.data.forEach((bar: any, idx: number) => {
          const val = ds.data[idx];

          // ── N/A label for null values ──────────────────────────────────────
          if (val == null) {
            ctx.save();
            ctx.font = "10px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillStyle = "#9ca3af";
            // Place at zero-line level
            const zeroY = chart.scales.y.getPixelForValue(0);
            ctx.fillText("N/A", bar.x, zeroY - 2);
            ctx.restore();
            return;
          }
          if (val === 0) return;

          ctx.save();
          ctx.textAlign = "center";
          ctx.fillStyle = "#1f2937";

          // ── Audit score bars: two-line "score\n(pct%)" ───────────────────
          if (ds._pcts) {
            const pct = ds._pcts[idx];
            const line1 = String(val);
            const line2 = pct != null ? "(" + (+pct.toFixed(0)) + "%)" : "";
            ctx.font = "bold 11px sans-serif";
            ctx.textBaseline = "bottom";
            ctx.fillText(line1, bar.x, bar.y - 14);
            ctx.font = "10px sans-serif";
            ctx.fillText(line2, bar.x, bar.y - 3);
          }
          // ── Incident bars with count: "pct% (count)" ─────────────────────
          else if (ds._actuals) {
            const actual = ds._actuals[idx];
            const pctStr = val + "%";
            const cntStr = actual != null ? " (" + actual + ")" : "";
            ctx.font = "bold 11px sans-serif";
            ctx.textBaseline = "bottom";
            ctx.fillText(pctStr + cntStr, bar.x, bar.y - 3);
          }
          // ── Regular pct bars ──────────────────────────────────────────────
          else {
            ctx.font = "bold 11px sans-serif";
            ctx.textBaseline = "bottom";
            ctx.fillText(val + "%", bar.x, bar.y - 3);
          }
          ctx.restore();
        });
      });
    } catch { /* never crash */ }
  },
};

// ── Off-screen chart renderer ────────────────────────────────────────────────
// IMPORTANT: Chart.js resizes the canvas's real pixel buffer internally
// based on devicePixelRatio *after* construction — if that ends up larger
// than the W×H we assume, a naive `drawImage(canvas, 0, 0)` into a W×H
// output canvas only copies the top-left crop of it (this was cutting off
// the right side of the legend and the whole x-axis in the PDF). We fix
// this two ways: (1) pin devicePixelRatio to 1 so Chart.js can't grow the
// buffer, and (2) as a safety net, always read the canvas's *actual*
// width/height at capture time rather than assuming it stayed at W×H.

const renderChart = (config: any, W = 1400, H = 520): Promise<string> =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    // Pin the CSS box to match the pixel buffer exactly.
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    canvas.style.cssText += "position:absolute;left:-9999px;top:-9999px;";
    document.body.appendChild(canvas);
    const chart = new ChartJS(canvas, {
      ...config,
      options: { ...config.options, devicePixelRatio: 1 },
    });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const actualW = canvas.width;
      const actualH = canvas.height;
      const out = document.createElement("canvas");
      out.width = actualW; out.height = actualH;
      const ctx = out.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, actualW, actualH);
      ctx.drawImage(canvas, 0, 0, actualW, actualH);
      const dataUrl = out.toDataURL("image/png");
      chart.destroy();
      document.body.removeChild(canvas);
      resolve(dataUrl);
    }));
  });

// ── Chart config factories ───────────────────────────────────────────────────
// Canvas size: 1600 × 600 — wide enough so grouped bars have breathing room
// All options mirror TrendsView exactly (same colors, labels, axis titles)

const CHART_W = 1600;
const CHART_H = 600;

// Chart 1: Fire Safety Scorecard — grouped bars per month
// Colors match screen: red / pink / orange / cyan / indigo
const fireSafetyConfig = (points: MonthPoint[]) => ({
  type: "bar" as const,
  data: {
    labels: points.map(p => p.label),
    datasets: [
      {
        label: "% External Audit (Cat-A NC Completion)",
        data: points.map(p => p.extACatNcPct != null ? +p.extACatNcPct.toFixed(1) : null),
        backgroundColor: "rgba(239,68,68,0.80)", borderColor: "#dc2626",
        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.75, categoryPercentage: 0.8,
      },
      {
        label: "% Internal Audit (Cat-A NC Completion)",
        data: points.map(p => p.intACatNcPct != null ? +p.intACatNcPct.toFixed(1) : null),
        backgroundColor: "rgba(251,113,133,0.85)", borderColor: "rgba(244,63,94,1)",
        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.75, categoryPercentage: 0.8,
      },
      {
        label: "Fire Safety Aggregate % Score",
        data: points.map(p => p.avgPct != null ? +p.avgPct.toFixed(1) : null),
        backgroundColor: "rgba(231,108,13,0.80)", borderColor: "#e89624",
        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.75, categoryPercentage: 0.8,
      },
      {
        label: "Near Miss %",
        data: points.map(p => p.nearMissPct != null ? +p.nearMissPct.toFixed(1) : null),
        _actuals: points.map(p => p.nearMissActual),
        backgroundColor: "rgba(68,202,239,0.80)", borderColor: "rgba(28,185,174,1)",
        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.75, categoryPercentage: 0.8,
      },
      {
        label: "Major Incident %",
        data: points.map(p => p.majorPct != null ? +p.majorPct.toFixed(1) : null),
        _actuals: points.map(p => p.majorActual),
        backgroundColor: "rgba(99,102,241,0.80)", borderColor: "rgba(79,70,229,1)",
        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.75, categoryPercentage: 0.8,
      },
    ],
  },
  options: {
    responsive: false, animation: false,
    layout: { padding: { top: 36, left: 16, right: 16, bottom: 12 } },
    plugins: {
      legend: { position: "top" as const, labels: { font: { size: 13 }, padding: 18, usePointStyle: true, pointStyle: "circle" } },
      title: { display: true, text: "Fire Safety Scorecard \u2014 Monthly Overview", font: { size: 15, weight: "bold" as const }, padding: { bottom: 16 }, color: "#1f2937" },
      tooltip: { enabled: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 0 } },
      y: {
        beginAtZero: true, max: 110,
        ticks: { font: { size: 12 }, callback: (v: any) => v + "%" },
        title: { display: true, text: "Percentage (%)", font: { size: 12 }, color: "#6b7280" },
        grid: { color: "rgba(0,0,0,0.06)" },
      },
    },
  },
  plugins: [barLabelPlugin],
});

// Chart 2: Audit Report — Score by Category over time, one grouped bar-set
// per MONTH (timeline on the x-axis), same as the Fire Safety chart.
// Bars within a month are packed tighter (higher barPercentage) since there
// are 6 series per group here instead of 5.
const auditTimelineConfig = (points: MonthPoint[]) => {
  return {
    type: "bar" as const,
    data: {
      labels: points.map(p => p.label),
      datasets: [
        {
          label: "Cat A \u2014 External",
          data: points.map(p => p.extA != null ? +p.extA.toFixed(1) : null),
          _pcts: points.map(p => p.extAvgPct),
          backgroundColor: "rgba(220,38,38,0.85)", borderColor: "#b91c1c",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
        {
          label: "Cat A \u2014 Internal",
          data: points.map(p => p.intA != null ? +p.intA.toFixed(1) : null),
          _pcts: points.map(p => p.intAvgPct),
          backgroundColor: "rgba(252,165,165,0.85)", borderColor: "rgba(239,68,68,0.9)",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
        {
          label: "Cat B \u2014 External",
          data: points.map(p => p.extB != null ? +p.extB.toFixed(1) : null),
          _pcts: points.map(p => p.extAvgPct),
          backgroundColor: "rgba(234,179,8,0.85)", borderColor: "#ca8a04",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
        {
          label: "Cat B \u2014 Internal",
          data: points.map(p => p.intB != null ? +p.intB.toFixed(1) : null),
          _pcts: points.map(p => p.intAvgPct),
          backgroundColor: "rgba(253,224,71,0.85)", borderColor: "rgba(234,179,8,0.9)",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
        {
          label: "Cat C \u2014 External",
          data: points.map(p => p.extC != null ? +p.extC.toFixed(1) : null),
          _pcts: points.map(p => p.extAvgPct),
          backgroundColor: "rgba(22,163,74,0.85)", borderColor: "#15803d",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
        {
          label: "Cat C \u2014 Internal",
          data: points.map(p => p.intC != null ? +p.intC.toFixed(1) : null),
          _pcts: points.map(p => p.intAvgPct),
          backgroundColor: "rgba(134,239,172,0.85)", borderColor: "rgba(22,163,74,0.9)",
          borderWidth: 1.5, borderRadius: 3, barPercentage: 0.95, categoryPercentage: 0.88,
        },
      ],
    },
    options: {
      responsive: false, animation: false,
      layout: { padding: { top: 44, left: 16, right: 16, bottom: 12 } },
      plugins: {
        legend: { position: "top" as const, labels: { font: { size: 13 }, padding: 18, usePointStyle: true, pointStyle: "circle" } },
        title: { display: true, text: "Audit Report \u2014 Score by Category (External & Internal)", font: { size: 15, weight: "bold" as const }, padding: { bottom: 16 }, color: "#1f2937" },
        tooltip: { enabled: false },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 0 } },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 12 }, callback: (v: any) => v },
          title: { display: true, text: "Score", font: { size: 12 }, color: "#6b7280" },
          grid: { color: "rgba(0,0,0,0.06)" },
        },
      },
    },
    plugins: [barLabelPlugin],
  };
};

// Chart 3: Electrical Scorecard — color-coded single bar per month
// Green ≥80%, Orange 60–79%, Red <60% — matches screen exactly
const electricalConfig = (points: MonthPoint[]) => ({
  type: "bar" as const,
  data: {
    labels: points.map(p => p.label),
    datasets: [{
      label: "Avg score %",
      data: points.map(p => p.elecAvgPct != null ? +p.elecAvgPct.toFixed(1) : null),
      backgroundColor: points.map(p =>
        p.elecAvgPct == null ? "rgba(156,163,175,0.4)"
          : p.elecAvgPct >= 80 ? "rgba(16,185,129,0.82)"
            : p.elecAvgPct >= 60 ? "rgba(245,158,11,0.82)"
              : "rgba(239,68,68,0.82)"
      ),
      borderColor: points.map(p =>
        p.elecAvgPct == null ? "#9ca3af"
          : p.elecAvgPct >= 80 ? "#059669"
            : p.elecAvgPct >= 60 ? "#d97706"
              : "#dc2626"
      ),
      borderWidth: 1.5, borderRadius: 4, barPercentage: 0.55, categoryPercentage: 0.8,
    }],
  },
  options: {
    responsive: false, animation: false,
    layout: { padding: { top: 36, left: 16, right: 16, bottom: 12 } },
    plugins: {
      legend: { position: "top" as const, labels: { font: { size: 13 }, padding: 18, usePointStyle: true, pointStyle: "circle" } },
      title: { display: true, text: "Electrical Scorecard \u2014 Avg score % per Month", font: { size: 15, weight: "bold" as const }, padding: { bottom: 16 }, color: "#1f2937" },
      tooltip: { enabled: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 0 } },
      y: {
        beginAtZero: true, max: 110,
        ticks: { font: { size: 12 }, callback: (v: any) => v + "%" },
        title: { display: true, text: "Compliance (%)", font: { size: 12 }, color: "#6b7280" },
        grid: { color: "rgba(0,0,0,0.06)" },
      },
    },
  },
  plugins: [barLabelPlugin],
});

// ── PDF layout constants ─────────────────────────────────────────────────────

const RANGE_LABEL: Record<Range, string> = { "1M": "1 Month", "3M": "3 Months", "6M": "6 Months", "12M": "12 Months" };
const PAGE_W = 297; const PAGE_H = 210; // A4 landscape mm

const pdfFilename = (): string => {
  const now = new Date();
  return MONTH_NAMES[now.getMonth()] + "_Fire_Safety_ScoreCard_TrendCharts.pdf";
};

function drawPageHeader(doc: jsPDF, premiseName: string, subtitle: string, address: string, pageNum: number, totalPages: number) {
  // Teal header band
  doc.setFillColor(0, 168, 165);
  doc.rect(0, 0, PAGE_W, 16, "F");
  // Premise name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12); doc.setFont("helvetica", "bold");
  doc.text(premiseName, 8, 11);
  // Subtitle right-aligned
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const tw = doc.getTextWidth(subtitle);
  doc.text(subtitle, PAGE_W - tw - 8, 11);
  doc.setTextColor(0, 0, 0);
  // Address
  if (address) {
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.setTextColor(90, 90, 90);
    doc.text(address, 8, 22);
    doc.setTextColor(0, 0, 0);
  }
  // Footer
  doc.setFontSize(7); doc.setFont("helvetica", "normal");
  doc.setTextColor(150, 150, 150);
  const gen = "Generated: " + new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(gen, PAGE_W - 8, PAGE_H - 4, { align: "right" });
  doc.text("Page " + pageNum + " of " + totalPages, 8, PAGE_H - 4);
  doc.setTextColor(0, 0, 0);
}

function embedChart(doc: jsPDF, imgUrl: string, x: number, y: number, w: number, h: number) {
  doc.addImage(imgUrl, "PNG", x, y, w, h);
}

// Fit image inside a box keeping aspect ratio
function fitImg(srcW: number, srcH: number, boxW: number, boxH: number): { w: number; h: number } {
  const ratio = srcW / srcH;
  let w = boxW; let h = w / ratio;
  if (h > boxH) { h = boxH; w = h * ratio; }
  return { w, h };
}

// ── Main PDF builder ─────────────────────────────────────────────────────────

interface BuildPdfOptions {
  premises: premisesApiResponse[];
  range: Range;
  onProgress?: (done: number, total: number) => void;
}

async function buildPdf({ premises, range, onProgress }: BuildPdfOptions): Promise<void> {
  const months = getMonths(range);
  // 3 pages per premise: 1=Fire Safety, 2=Audit Report, 3=Electrical
  const totalPages = premises.length * 3;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  let firstPage = true;
  let globalPage = 0;

  // A4 landscape: 297 × 210 mm
  // Header band: 16 mm. Address line: to ~23 mm. Footer: last 7 mm.
  // Usable chart area: y=25 to y=202  → height = 177 mm
  const CONTENT_TOP = 25;
  const CONTENT_BOT = 202;
  const CHART_H_MM = CONTENT_BOT - CONTENT_TOP; // 177 mm
  const CHART_W_MM = PAGE_W - 16;               // 281 mm  (8 mm margins)
  const CHART_X = 8;

  for (let i = 0; i < premises.length; i++) {
    const premise = premises[i];
    const premiseId = String((premise as any)._id || (premise as any).id);
    const premiseName = premise.premisesName || premiseId;
    onProgress?.(i, premises.length);

    // Fetch fire-safety and electrical scorecards in parallel
    const [fsR1, fsR2, elR1, elR2] = await Promise.all([
      safeFetchAll("/api/fire-safety-scorecards", { premisesId: premiseId }),
      safeFetchAll("/api/fire-safety-scorecards", { premise: premiseId }),
      safeFetchAll("/api/electrical-safety-scorecards", { premisesId: premiseId }),
      safeFetchAll("/api/electrical-safety-scorecards", { premise: premiseId }),
    ]);

    const dedup = (a: any[], b: any[]) => {
      const seen = new Set<string>(); const out: any[] = [];
      [...a, ...b].forEach(r => {
        const id = r._id || r.id || JSON.stringify(r);
        if (!seen.has(id)) { seen.add(id); out.push(r); }
      });
      return out;
    };

    // Some scorecard endpoints ignore the premisesId/premise query params
    // server-side and just return every record — filter locally so each
    // premise's pages only reflect that premise's own data.
    const fsRecords = dedup(fsR1, fsR2).filter(r => belongsToPremise(r, premiseId));
    const elRecords = dedup(elR1, elR2).filter(r => belongsToPremise(r, premiseId));

    const firePoints = buildFirePoints(fsRecords, months);
    const fullPoints = mergeElecPoints(elRecords, months, firePoints);

    const hasFireData = firePoints.some(p =>
      p.avgPct != null || p.extACatNcPct != null || p.nearMissPct != null
    );
    const hasAuditData = firePoints.some(p =>
      p.extA != null || p.intA != null || p.extB != null
    );
    const hasElecData = fullPoints.some(p => p.elecAvgPct != null);

    const addrParts: string[] = [];
    if ((premise as any).addressLine1) addrParts.push((premise as any).addressLine1);
    if ((premise as any).townCity) addrParts.push((premise as any).townCity);
    if ((premise as any).region) addrParts.push((premise as any).region);
    const address = addrParts.join(", ");
    const rangeLabel = RANGE_LABEL[range];

    // Helper to add a standard page and draw header
    const addPage = (chartTitle: string) => {
      globalPage++;
      if (!firstPage) doc.addPage(); firstPage = false;
      drawPageHeader(
        doc, premiseName,
        rangeLabel + "  \u2022  " + chartTitle + "  |  " + globalPage + " / " + totalPages,
        address, globalPage, totalPages
      );
    };

    // Helper to embed a rendered chart centred in the content area
    const embedFull = (imgUrl: string) => {
      const { w, h } = fitImg(CHART_W, CHART_H, CHART_W_MM, CHART_H_MM);
      const x = CHART_X + (CHART_W_MM - w) / 2;
      const y = CONTENT_TOP + (CHART_H_MM - h) / 2;
      doc.addImage(imgUrl, "PNG", x, y, w, h);
    };

    // ── PAGE 1: Fire Safety Scorecard ───────────────────────────────────────
    addPage("Fire Safety Scorecard");
    if (!hasFireData) {
      doc.setFontSize(11); doc.setFont("helvetica", "italic"); doc.setTextColor(180, 180, 180);
      doc.text("No Fire Safety Scorecard data for the selected period.", PAGE_W / 2, PAGE_H / 2, { align: "center" });
      doc.setTextColor(0, 0, 0);
    } else {
      const img = await renderChart(fireSafetyConfig(firePoints), CHART_W, CHART_H);
      embedFull(img);
    }

    // ── PAGE 2: Audit Report — Score by Category (timeline) ─────────────────
    addPage("Audit Report");
    if (!hasAuditData) {
      doc.setFontSize(11); doc.setFont("helvetica", "italic"); doc.setTextColor(180, 180, 180);
      doc.text("No Audit Report data for the selected period.", PAGE_W / 2, PAGE_H / 2, { align: "center" });
      doc.setTextColor(0, 0, 0);
    } else {
      const img = await renderChart(auditTimelineConfig(firePoints), CHART_W, CHART_H);
      embedFull(img);
    }

    // ── PAGE 3: Electrical Safety Scorecard ──────────────────────────────────
    addPage("Electrical Safety Scorecard");
    if (!hasElecData) {
      doc.setFontSize(11); doc.setFont("helvetica", "italic"); doc.setTextColor(180, 180, 180);
      doc.text("No Electrical Safety Scorecard data for the selected period.", PAGE_W / 2, PAGE_H / 2, { align: "center" });
      doc.setTextColor(0, 0, 0);
    } else {
      const img = await renderChart(electricalConfig(fullPoints), CHART_W, CHART_H);
      embedFull(img);
      // Colour legend strip
      const { w, h } = fitImg(CHART_W, CHART_H, CHART_W_MM, CHART_H_MM);
      const chartY = CONTENT_TOP + (CHART_H_MM - h) / 2;
      const legendY = chartY + h + 3;
      if (legendY + 5 < PAGE_H - 6) {
        const legendItems = [
          { r: 16, g: 185, b: 129, label: "\u2265 80%  Good" },
          { r: 245, g: 158, b: 11, label: "60\u201379%  Moderate" },
          { r: 239, g: 68, b: 68, label: "< 60%  Needs Attention" },
        ];
        let lx = CHART_X + (CHART_W_MM - w) / 2;
        legendItems.forEach(({ r, g, b, label }) => {
          doc.setFillColor(r, g, b);
          doc.roundedRect(lx, legendY, 3.5, 3.5, 0.5, 0.5, "F");
          doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
          doc.text(label, lx + 4.5, legendY + 2.8);
          lx += 4.5 + doc.getTextWidth(label) + 8;
          doc.setTextColor(0, 0, 0);
        });
      }
    }
  }

  onProgress?.(premises.length, premises.length);
  doc.save(pdfFilename());
}

// ── React component ───────────────────────────────────────────────────────────

interface Props { premises: premisesApiResponse[]; }

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "12M", label: "12M" },
];

const TrendChartsPdfDownload: React.FC<Props> = ({ premises }) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<Range>("6M");
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const handleDownload = useCallback(async () => {
    if (!premises.length || generating) return;
    setGenerating(true);
    setProgress({ done: 0, total: premises.length });
    try {
      await buildPdf({ premises, range, onProgress: (done, total) => setProgress({ done, total }) });
      setOpen(false);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  }, [premises, range, generating]);

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#00A8A5] text-white rounded-lg text-sm font-semibold hover:bg-[#008A87] transition-colors shadow-sm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
        Trend Charts of all premises
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !generating && setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal header */}
            <div className="bg-[#00A8A5] px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                  <polyline points="16 7 22 7 22 13" />
                </svg>
                <h3 className="text-white font-semibold text-base">Trend Charts PDF</h3>
              </div>
              {!generating && (
                <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {/* Modal body */}
            <div className="px-6 py-3 flex flex-col gap-5">
              <div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Download a PDF with trend charts for{" "}
                  <span className="font-semibold text-gray-700">all {premises.length} premise{premises.length !== 1 ? "s" : ""}</span>.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Each premise gets 3 pages — one full-page chart each.
                </p>
              </div>

              {/* Range selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Time Range</label>
                <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                  {RANGE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setRange(opt.value)}
                      disabled={generating}
                      className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${range === opt.value
                          ? "bg-white text-[#00A8A5] shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Last {RANGE_LABEL[range].toLowerCase()} up to today.</p>
              </div>

              {/* Included charts info */}
              <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Included charts (per premise)</p>
                {[
                  { dot: "bg-orange-400", text: "Page 1 — Fire Safety Scorecard: NC completion, aggregate score, near miss & major incident %" },
                  { dot: "bg-red-400", text: "Page 2 — Audit Report: External & Internal scores per category (A / B / C) over time" },
                  { dot: "bg-green-500", text: "Page 3 — Electrical Safety Scorecard: avg compliance % per month" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.dot}`} />
                    <span className="text-xs text-gray-500">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              {generating && progress && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Processing premise {progress.done} of {progress.total}...</span>
                    <span>{Math.round((progress.done / progress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-[#00A8A5] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.done / progress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Rendering charts... this may take a moment.</p>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-3">
              <button onClick={() => setOpen(false)} disabled={generating}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40">
                Cancel
              </button>
              <button onClick={handleDownload} disabled={generating || premises.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#00A8A5] hover:bg-[#008A87] rounded-lg transition-colors disabled:opacity-50">
                {generating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrendChartsPdfDownload;